import {
  ADMINISTRATOR,
  hasAnyRole,
  SUBJECT_WRITE_ROLES,
  SYSTEM_ADMINISTRATOR,
} from '@/shared/guards/roles';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';

/**
 * `true` se o usuário atual pode EXECUTAR ações sobre subjects (taxonomia) —
 * caminho crítico restrito a SYSTEM_ADMINISTRATOR mesmo para quem tem
 * `ADMINISTRATOR`. É o único gate de escrita que sobra dentro do Content Hub:
 * o eixo `contentEditor` × `contentViewer` foi removido do catálogo em
 * `20260904120000-remove-unused-admin-roles`, então quem abre as telas de
 * conteúdo pode agir nelas. É só UX — o backend
 * (`StrictRoles('adminDashboard.systemAdministrator')` nos controllers de
 * subject/subject-edge/subject-view/subject-view-membership) é a autoridade real.
 *
 * Havia um `useCanWriteContent` ao lado deste, para provas/questões. Ele saiu
 * junto com `CONTENT_WRITE_ROLES` em `085e12e1`, mas quatro chamadas ficaram
 * importando o que já não existia — o módulo `content` não compilava. As quatro
 * foram removidas em vez de o hook ser ressuscitado, que é a conclusão do
 * próprio commit: **a tela de instituições, provas e questões não tem gate de
 * escrita**; o único gate dentro do hub é o de taxonomia, acima.
 */
export function useCanWriteSubjects(): boolean {
  const roles = useEffectiveRoles();
  return hasAnyRole(roles, SUBJECT_WRITE_ROLES);
}

/**
 * `true` se o usuário atual pode **mesclar instituições**.
 *
 * Compartilha `SUBJECT_WRITE_ROLES` com a taxonomia de propósito: as duas
 * espelham `@StrictRoles('adminDashboard.systemAdministrator')` no backend, e
 * uma segunda constante com o mesmo conteúdo divergiria na primeira mudança.
 * A mescla entrou nessa alçada porque reescreve o `body` de N provas em massa
 * (ver `gerenciamento-de-instituicoes.md` §10) — é a única rota do CRUD de
 * instituição que não abre para `administrator`.
 *
 * É só UX: o gate real é o `StrictRoles` da rota. Existe para o botão não
 * mentir — oferecer uma ação irreversível que vai voltar 403 é pior do que não
 * oferecer.
 */
export function useCanMergeInstitutions(): boolean {
  const roles = useEffectiveRoles();
  return hasAnyRole(roles, SUBJECT_WRITE_ROLES);
}

/**
 * `true` se o usuário atual vê o form "relacionar por id" da ficha de um
 * conteúdo. Quem autoriza a escrita é o `@Roles` do
 * `content-relation-admin.controller.ts` (monolith), nas rotas `POST`/`DELETE
 * links` — hoje só os dois curingas o atravessam, e este gate coincide com ele.
 *
 * As duas keys vão listadas porque no front o curinga é só o
 * `SYSTEM_ADMINISTRATOR`. **Revisar quando a tela ganhar uma role mais ampla**:
 * a role nova não vê o form até alguém decidir que deve. É só UX.
 */
export function useCanLinkContent(): boolean {
  const roles = useEffectiveRoles();
  return hasAnyRole(roles, [ADMINISTRATOR, SYSTEM_ADMINISTRATOR]);
}
